import {round2} from '@vat/engine/rounding';
import type {
    BookingArtLite,
    BookingLite,
    BookingStatusLite,
    BookingTypeLite,
    ExpenseLite,
    OwnershipWindow,
    PaymentLite,
    PendingLandlordChange,
    RentReductionLite,
    UnitAreaChangeLite,
    UnitLite
} from '@vat/engine/types';
import {normalizeToValidDateOrNull} from 'app/helpers';
import {UNIT_AREA_TIMEZONE} from 'app/shared/helpers/unit-area/unit-area-timeline';
import {DateTime} from 'luxon';
import type {BookingPayment} from 'app/modules/admin/booking/features/payments/models/bookingPayment';
import type {Booking} from 'app/modules/admin/booking/models/booking';
import type {RentReduction} from 'app/modules/admin/booking/models/rentReduction';
import type {NahausBookingsAccount} from 'app/modules/admin/bookingsAccounts/models/nahausBookingsAccount.interface';
import type {Expense} from 'app/modules/admin/expenses/models/expenses.interface';
import type {LandlordChange} from 'app/modules/admin/properties/features/landlord_change/models/landlordChange.interface';
import type {PropertyUnit, UnitAreaChange} from '@units/models/unit.interface';

/**
 * Einzige Brücke zwischen den Firestore-Interfaces (Booking, BookingPayment, Expense, PropertyUnit,
 * LandlordChange) und den Lite-Typen des Rechenkerns (Plan 1). Timestamps → Date | null über
 * normalizeToValidDateOrNull, fehlende Felder explizit null, Vorzeichen bleiben wie gespeichert
 * (die Engine setzt PAYOUT selbst negativ, R4/R6). Kein Angular, kein Firebase.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export const finiteOrNull = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

export const boolOrNull = (value: unknown): boolean | null => typeof value === 'boolean' ? value : null;

const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Spiegel der String-Werte von BookingArtOptions (BK ist als 'BETRIEBSKOSTEN' gespeichert). */
export const BOOKING_ART_LITE_VALUES: ReadonlySet<string> = new Set<BookingArtLite>([
    'WARM_MIETE', 'KALT_MIETE', 'KAUTION', 'BETRIEBSKOSTEN', 'DUNNING_FEES', 'INTEREST_EXPENSES', 'SP', 'CUSTOM', 'GUTHABEN',
]);

export const normalizeArt = (art: unknown): BookingArtLite | null =>
    typeof art === 'string' && BOOKING_ART_LITE_VALUES.has(art) ? art as BookingArtLite : null;

export const normalizeType = (type: unknown): BookingTypeLite | null =>
    type === 'PAYMENT' || type === 'PAYOUT' ? type : null;

const STATUS_VALUES: ReadonlySet<string> = new Set<BookingStatusLite>(['PAID', 'NOT_PAID', 'PARTIALLY_PAID', 'EXPECTED', 'IGNORED']);

export const normalizeStatus = (status: unknown): BookingStatusLite | null =>
    typeof status === 'string' && STATUS_VALUES.has(status) ? status as BookingStatusLite : null;

/** R9: Darlehenskonten liefern nie Vorsteuer (Zins, Tilgung, Darlehensrate). */
export const isLoanAccount = (account: Partial<NahausBookingsAccount> | null | undefined): boolean =>
    account?.accountType === 'LOANS' || account?.isLoanInterest === true || account?.isLoanRepayment === true;

export const toRentReductionLite = (reduction: RentReduction): RentReductionLite => ({
    id: reduction?.id ?? '',
    amount: Math.abs(finiteOrNull(reduction?.amount) ?? 0),
    date: normalizeToValidDateOrNull(reduction?.date),
    createdAt: normalizeToValidDateOrNull(reduction?.createdAt),
});

export const toBookingLite = (booking: Booking): BookingLite => {
    const amount = finiteOrNull(booking?.amount);
    const haben = finiteOrNull(booking?.haben) ?? 0;
    const division = booking?.divisionWarm;
    return {
        id: booking?.id ?? '',
        propertyID: booking?.propertyID ?? '',
        unitID: booking?.unitID ?? null,
        periodID: booking?.periodID ?? null,
        date: normalizeToValidDateOrNull(booking?.date),
        createdAt: normalizeToValidDateOrNull(booking?.createdAt),
        amount,
        haben,
        saldo: finiteOrNull(booking?.saldo) ?? round2(haben - (amount ?? 0)),
        rentReductionAmount: finiteOrNull(booking?.rentReductionAmount) ?? 0,
        rentReductionList: Array.isArray(booking?.rentReductionList) ? booking.rentReductionList.map(toRentReductionLite) : [],
        divisionWarm: division
            ? {kalt: finiteOrNull(division.kalt), bk: finiteOrNull(division.bk), hk: finiteOrNull(division.hk)}
            : null,
        art: normalizeArt(booking?.art),
        type: normalizeType(booking?.type),
        status: normalizeStatus(booking?.status),
        text: typeof booking?.text === 'string' && booking.text.length ? booking.text : null,
        sollMonth: typeof booking?.sollMonth === 'string' && MONTH_KEY.test(booking.sollMonth) ? booking.sollMonth : null,
        includesVAT: boolOrNull(booking?.includesVAT),
        vat: finiteOrNull(booking?.vat),
    };
};

export const toPaymentLite = (payment: BookingPayment): PaymentLite => ({
    id: payment?.id ?? '',
    bookingID: payment?.bookingID ?? null,
    propertyID: payment?.propertyID ?? '',
    unitID: payment?.unitID ?? null,
    periodID: payment?.periodID ?? null,
    date: normalizeToValidDateOrNull(payment?.date),
    amount: finiteOrNull(payment?.amount),
    art: normalizeArt(payment?.art),
    type: normalizeType(payment?.type),
    isInternal: payment?.isInternal === true,
    transferType: typeof payment?.transferType === 'string' ? payment.transferType : null,
    text: typeof payment?.text === 'string' && payment.text.length ? payment.text : (payment?.bookingText ?? null),
    includesVAT: boolOrNull(payment?.includesVAT),
    vat: finiteOrNull(payment?.vat),
});

export const toExpenseLite = (expense: Expense): ExpenseLite => ({
    id: expense?.id ?? '',
    propertyID: expense?.property?.id ?? '',
    unitsID: Array.isArray(expense?.unitsID) ? expense.unitsID.filter(id => typeof id === 'string' && id.length) : [],
    hasAllUnitsSelected: expense?.hasAllUnitsSelected === true,
    type: expense?.type === 'CREDIT' ? 'CREDIT' : 'EXPENSE',
    amount: finiteOrNull(expense?.amount),
    includesVAT: boolOrNull(expense?.includesVAT),
    vatRate: finiteOrNull(expense?.vatRate),
    vatAmount: finiteOrNull(expense?.vatAmount),
    date: normalizeToValidDateOrNull(expense?.date),
    invoiceDate: normalizeToValidDateOrNull(expense?.invoiceDate),
    serviceEndDate: normalizeToValidDateOrNull(expense?.serviceEndDate),
    isLoanAccount: isLoanAccount(expense?.nahausBookingsAccount),
    description: typeof expense?.description === 'string' && expense.description.length ? expense.description : (expense?.category ?? null),
    accountRefID: nonEmptyStringOrNull(expense?.nahausBookingsAccount?.refID),
    accountLabel: nonEmptyStringOrNull(expense?.nahausBookingsAccount?.label),
    accountType: nonEmptyStringOrNull(expense?.nahausBookingsAccount?.accountType),
});

const nonEmptyStringOrNull = (value: unknown): string | null => typeof value === 'string' && value.length ? value : null;

/** Anzeigename einer Einheit: label > w_id_text > „W{w_id}“ > null. */
export const unitLabel = (unit: Pick<PropertyUnit, 'label' | 'w_id_text' | 'w_id'> | null | undefined): string | null =>
    nonEmptyStringOrNull(unit?.label)
    ?? nonEmptyStringOrNull(unit?.w_id_text)
    ?? (typeof unit?.w_id === 'number' && Number.isFinite(unit.w_id) ? `W${unit.w_id}` : null);

/**
 * Flächen-Zeitleiste: alle Einträge unabhängig vom Status (SCHEDULED/APPLIED ist nur der Cron-Merker – das Datum
 * entscheidet, wie im gemeinsamen Helper `resolveAreaAt`), `value` → `area` (endlich und > 0, sonst ignoriert),
 * `startDate` → lokale Mitternacht des Berliner Kalendertags (vergleichbar mit `monthStart`) | null,
 * sortiert (Ausgangsfläche ohne startDate zuerst, dann aufsteigend). Leer → null.
 */
const toLocalCalendarDay = (date: Date | null): Date | null => {
    if (!date) {
        return null;
    }
    const berlin = DateTime.fromJSDate(date, {zone: UNIT_AREA_TIMEZONE});
    return new Date(berlin.year, berlin.month - 1, berlin.day);
};

/** NaN-sicherer Zahlenvergleich (−∞ − −∞ wäre NaN und würde die Sortierung undefiniert machen). */
const compareNumbers = (a: number, b: number): number => (a === b ? 0 : a < b ? -1 : 1);

/**
 * Sortierung wie `normalizeAreaChanges` (shared/helpers/unit-area): Ausgangsfläche ohne startDate zuerst, dann
 * startDate aufsteigend, gleicher Tag nach createdAt aufsteigend (fehlendes createdAt zuerst). Damit gewinnt in
 * `unitAreaForMonth` (nimmt bei gleichem Tag den letzten Array-Eintrag) dieselbe Zeile wie im gemeinsamen Helper:
 * die zuletzt erfasste. `createdAt` wird nur zum Sortieren gelesen und nicht in den Lite-Typ übernommen.
 */
export const toAreaChangesLite = (changes: UnitAreaChange[] | null | undefined): UnitAreaChangeLite[] | null => {
    if (!Array.isArray(changes)) {
        return null;
    }
    const lite = changes
        .filter(change => !!change)
        .map(change => ({
            startDate: toLocalCalendarDay(normalizeToValidDateOrNull(change.startDate)),
            area: finiteOrNull(Number(change.value)),
            createdMs: normalizeToValidDateOrNull(change.createdAt)?.getTime() ?? Number.NEGATIVE_INFINITY,
        }))
        .filter(change => change.area !== null && change.area > 0)
        .sort((a, b) =>
            compareNumbers(a.startDate?.getTime() ?? Number.NEGATIVE_INFINITY, b.startDate?.getTime() ?? Number.NEGATIVE_INFINITY)
            || compareNumbers(a.createdMs, b.createdMs))
        .map((change): UnitAreaChangeLite => ({startDate: change.startDate, area: change.area as number}));
    return lite.length ? lite : null;
};

export const toUnitLite = (unit: PropertyUnit, propertyID: string): UnitLite => ({
    id: unit?.id ?? '',
    propertyID: unit?.propertyID ?? propertyID,
    area: finiteOrNull(unit?.area),
    areaChanges: toAreaChangesLite(unit?.areaChanges),
    label: unitLabel(unit),
    categoryType: typeof unit?.categoryType === 'string' ? unit.categoryType : null,
    landlordID: unit?.landlordID ?? unit?.landlord?.id ?? null,
});

/** Der Cloud-Function-Runner liest `status`; das Client-Interface trägt den Tippfehler `statues`. */
export const landlordChangeStatus = (change: LandlordChange): 'SCHEDULED' | 'COMPLETED' | 'ERROR' | null => {
    const raw = (change as unknown as { status?: string })?.status ?? change?.statues ?? null;
    return raw === 'SCHEDULED' || raw === 'COMPLETED' || raw === 'ERROR' ? raw : null;
};

export interface OwnershipResult {
    windows: OwnershipWindow[];
    pending: PendingLandlordChange[];
}

/**
 * R8: Eigentümerfenster je Immobilie aus ausgeführten Wechseln (`to` = performAt, exklusiv);
 * SCHEDULED mit performAt < now → pendingChanges (BLOCKER LANDLORD_CHANGE_PENDING).
 */
export const toOwnership = (propertyID: string, currentLandlordID: string | null, changes: LandlordChange[], now: Date): OwnershipResult => {
    const withDate = (changes ?? [])
        .map(change => ({change, performAt: normalizeToValidDateOrNull(change?.performAt), status: landlordChangeStatus(change)}))
        .filter(entry => entry.performAt !== null);

    const executed = withDate
        .filter(entry => entry.status === 'COMPLETED')
        .sort((a, b) => a.performAt.getTime() - b.performAt.getTime());

    const pending: PendingLandlordChange[] = withDate
        .filter(entry => entry.status === 'SCHEDULED' && entry.performAt.getTime() < now.getTime())
        .map(entry => ({propertyID, performAt: entry.performAt}));

    if (executed.length === 0) {
        return {windows: currentLandlordID ? [{propertyID, landlordID: currentLandlordID, from: null, to: null}] : [], pending};
    }

    const windows: OwnershipWindow[] = executed.map((entry, index) => ({
        propertyID,
        landlordID: entry.change.beforeLandlord?.id ?? '',
        from: index === 0 ? null : executed[index - 1].performAt,
        to: entry.performAt,
    }));
    const last = executed[executed.length - 1];
    windows.push({propertyID, landlordID: last.change.afterLandlord?.id ?? currentLandlordID ?? '', from: last.performAt, to: null});
    return {windows: windows.filter(window => window.landlordID.length > 0), pending};
};
